import React, { useEffect, useState, useRef } from 'react';
import { Input, Select, Space, Popconfirm, message, Form, Card } from 'antd';
import { fetchSqlData, updateData, insertData } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { UploadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CsvExcelImport from '../../components/common/CsvExcelImport';
import PrintButton from '../../components/common/PrintButton';
import StandardTable from '../../components/common/StandardTable';
import Button from '../../components/common/Button';

const { Option } = Select;

const ChemicalMaterialList: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState<number | undefined>(undefined);
  const [supplierFilter, setSupplierFilter] = useState('');
  const navigate = useNavigate();
  const [addForm] = Form.useForm();
  const [adding, setAdding] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMaterials();
    fetchUnits();
  }, []);

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const res = await fetchSqlData('mat_chemical_material');
      setData(Array.isArray(res) ? res : res.data || []);
    } catch {
      message.error(t('chemicalMaterials.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await fetchSqlData('cor_units');
      setUnits(Array.isArray(res) ? res : res.data || []);
    } catch {
      setUnits([]);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await updateData('mat_chemical_material', id, { deleted: true }); // Soft delete
      message.success(t('chemicalMaterials.deleteSuccess'));
      fetchMaterials();
    } catch {
      message.error(t('chemicalMaterials.deleteError'));
    }
  };

  const getUnitName = (unit_id: number) => {
    const unit = units.find((u: any) => u.id === unit_id);
    return unit ? unit.name : '-';
  };

  const filteredData = data.filter((item) => {
    const searchVal = globalSearch.toLowerCase();
    const matchesGlobal =
      item.material_no?.toString().toLowerCase().includes(searchVal) ||
      (item.description || '').toLowerCase().includes(searchVal) ||
      (item.quantity ? item.quantity.toString().toLowerCase().includes(searchVal) : false) ||
      (item.supplier || '').toLowerCase().includes(searchVal) ||
      getUnitName(item.unit_id).toLowerCase().includes(searchVal);
    const matchesUnit = unitFilter ? item.unit_id === unitFilter : true;
    const matchesSupplier = supplierFilter ? (item.supplier || '').toLowerCase().includes(supplierFilter.toLowerCase()) : true;
    return matchesGlobal && matchesUnit && matchesSupplier;
  });

  // Hızlı ekleme fonksiyonu (api.ts ile, token otomatik)
  const handleQuickAdd = async (values: any) => {
    setAdding(true);
    try {
      // 1. Önce material_no ile kayıt var mı kontrol et (GET ile)
      const url = `/api/dbExchange/get?table_name=mat_chemical_material&field_name=material_no&field_value=${encodeURIComponent(values.material_no)}`;
      const searchRes = await fetch(url);
      const searchData = await searchRes.json();
      if (Array.isArray(searchData) && searchData.length > 0) {
        // Kayıt var, quantity'yi topla ve update et
        const existing = searchData[0];
        const newQuantity = (parseFloat(existing.quantity) || 0) + (parseFloat(values.quantity) || 0);
        await updateData('mat_chemical_material', existing.id, { quantity: newQuantity });
        message.success(t('chemicalMaterials.updateSuccess'));
        fetchMaterials();
        addForm.resetFields();
      } else {
        // Kayıt yok, yeni kayıt olarak ekle
        await insertData('mat_chemical_material', {
          material_no: values.material_no,
          description: values.description,
          quantity: values.quantity,
          unit_id: units.find((u:any) => u.symbol === values.unit || u.name === values.unit)?.id,
          supplier: values.supplier
        });
        message.success(t('chemicalMaterials.addSuccess'));
        fetchMaterials();
        addForm.resetFields();
      }
    } catch (err) {
      message.error(t('chemicalMaterials.unexpectedError'));
    } finally {
      setAdding(false);
    }
  };

  const columns = [
    { title: t('chemicalMaterials.colMaterialNo'), dataIndex: 'material_no', key: 'material_no', width: 120 },
    { title: t('chemicalMaterials.colDescription'), dataIndex: 'description', key: 'description', width: 260 },
    { title: t('chemicalMaterials.colQuantity'), dataIndex: 'quantity', key: 'quantity', width: 110 },
    { title: t('chemicalMaterials.colUnit'), dataIndex: 'unit_id', key: 'unit_id', render: (val: number) => getUnitName(val), width: 110 },
    { title: t('chemicalMaterials.colSupplier'), dataIndex: 'supplier', key: 'supplier', width: 160 },
    { title: t('chemicalMaterials.col12Month'), dataIndex: 'for_12_months_operation', key: 'for_12_months_operation', width: 130 },
    { title: t('chemicalMaterials.colRemaining'), dataIndex: 'remain', key: 'remain', width: 110 },
    { title: t('chemicalMaterials.colCreatedAt'), dataIndex: 'created_at', key: 'created_at', render: (val: string) => val ? new Date(val).toLocaleDateString('tr-TR') : '-', width: 110 },
  ];

  return (
    <div>
      <h2 style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        {t('chemicalMaterials.pageTitle')}
      </h2>
      {/* Hızlı ekleme başlığı */}
      <div style={{fontWeight:600, fontSize:15, margin:'16px 0 4px 0'}}>{t('chemicalMaterials.quickAddTitle')}</div>
      {/* Hızlı ekleme alanı */}
      <div style={{margin:'8px 0 16px 0', padding:'12px', background:'#fff', border:'1px solid #eee', borderRadius:6}}>
        <Form form={addForm} layout="inline" onFinish={handleQuickAdd} style={{display:'flex', flexWrap:'wrap', alignItems:'flex-end', gap:8}}>
          <div style={{display:'flex', flexDirection:'column', minWidth:120}}>
            <span style={{fontWeight:500, fontSize:13}}>{t('chemicalMaterials.colMaterialNo')}</span>
            <Form.Item name="material_no" rules={[{required:true, message:t('chemicalMaterials.required')}]} style={{marginBottom:0}}>
              <Input placeholder={t('chemicalMaterials.colMaterialNo')} />
            </Form.Item>
          </div>
          <div style={{display:'flex', flexDirection:'column', minWidth:160}}>
            <span style={{fontWeight:500, fontSize:13}}>{t('chemicalMaterials.colDescription')}</span>
            <Form.Item name="description" rules={[{required:true, message:t('chemicalMaterials.required')}]} style={{marginBottom:0}}>
              <Input placeholder={t('chemicalMaterials.colDescription')} />
            </Form.Item>
          </div>
          <div style={{display:'flex', flexDirection:'column', minWidth:100}}>
            <span style={{fontWeight:500, fontSize:13}}>{t('chemicalMaterials.colQuantity')}</span>
            <Form.Item name="quantity" rules={[{required:true, message:t('chemicalMaterials.required')}]} style={{marginBottom:0}}>
              <Input type="number" placeholder={t('chemicalMaterials.colQuantity')} min={0} />
            </Form.Item>
          </div>
          <div style={{display:'flex', flexDirection:'column', minWidth:170}}>
            <span style={{fontWeight:500, fontSize:13}}>{t('chemicalMaterials.colUnit')}</span>
            <Form.Item name="unit" rules={[{required:true, message:t('chemicalMaterials.required')}]} style={{marginBottom:0}}>
              <Select showSearch placeholder={t('chemicalMaterials.colUnit')} optionFilterProp="children" style={{minWidth:150}}>
                {units.map((u:any) => (
                  <Select.Option key={u.id} value={u.symbol || u.name}>{u.symbol ? `${u.symbol} (${u.name})` : u.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div style={{display:'flex', flexDirection:'column', minWidth:140}}>
            <span style={{fontWeight:500, fontSize:13}}>{t('chemicalMaterials.colSupplier')}</span>
            <Form.Item name="supplier" style={{marginBottom:0}}>
              <Input placeholder={t('chemicalMaterials.colSupplier')} />
            </Form.Item>
          </div>
          <Form.Item style={{marginBottom:0}}>
            <Button action="add" type="primary" htmlType="submit" loading={adding}>{t('chemicalMaterials.add')}</Button>
          </Form.Item>
        </Form>
        <div style={{marginTop:16, display:'flex', gap:8, flexWrap:'wrap'}}>
          <CsvExcelImport tableName="mat_chemical_material" onSuccess={fetchMaterials} />
          <Button onClick={() => window.open(process.env.PUBLIC_URL + '/chemical_material_template.xlsx', '_blank')}>{t('chemicalMaterials.downloadExcel')}</Button>
          <Button onClick={() => window.open(process.env.PUBLIC_URL + '/chemical_material_template.csv', '_blank')}>{t('chemicalMaterials.downloadCsv')}</Button>
        </div>
      </div>
      <div style={{margin:'16px 0', padding:'12px', background:'#f6f6f6', border:'1px solid #eee', borderRadius:6}}>
        <b>{t('chemicalMaterials.uploadInfoTitle')}</b><br/>
        - {t('chemicalMaterials.uploadInfoFormat')}<br/>
        - {t('chemicalMaterials.uploadRequiredHeaders')} <b>material_no, description, quantity, unit, supplier, for_12_months_operation, remain</b><br/>
        - {t('chemicalMaterials.uploadInfoUnitValues')}<br/>
        - {t('chemicalMaterials.uploadInfoUnitNames')}<br/>
      </div>
      <div style={{position:'sticky',top:0,zIndex:20,background:'#f7fafd',paddingTop:8,paddingBottom:2}}>
        <Space style={{ flexWrap: 'wrap', width: '100%', justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <Input.Search
              placeholder={t('chemicalMaterials.globalSearchPlaceholder')}
              value={globalSearch}
              onChange={e => setGlobalSearch(e.target.value)}
              allowClear
              style={{ width: 260 }}
            />
            <Select
              placeholder={t('chemicalMaterials.unitFilterPlaceholder')}
              allowClear
              style={{ width: 180 }}
              value={unitFilter}
              onChange={setUnitFilter}
            >
              {units.map((u: any) => (
                <Option key={u.id} value={u.id}>{u.symbol ? `${u.symbol} (${u.name})` : u.name}</Option>
              ))}
            </Select>
            <Input
              placeholder={t('chemicalMaterials.supplierSearchPlaceholder')}
              value={supplierFilter}
              onChange={e => setSupplierFilter(e.target.value)}
              style={{ width: 180 }}
            />
          </div>
          <PrintButton targetRef={listRef} tooltip={t('chemicalMaterials.printList')} style={{marginLeft:8}} />
        </Space>
      </div>
      <div ref={listRef}>
        <StandardTable
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content', y: 520 }}
          sticky
          exportFileName="chemical_materials"
        />
      </div>
    </div>
  );
};

export default ChemicalMaterialList; 